<template lang="pug">
.input-split-date-time.register-row
  .register-group
    .register-key
      span.key-name {{ keyName }}
      span.validation-label(v-if="required == true") 必須

    .register-value 
      // year
      select#year.register-form.year(
        name="year"
        v-model="_year"
        v-validate="{ required: required }"
        placeholder="年"
        data-vv-as="年"
        :class="{ noinput: (required == true && !_year), disable: getYearDisable(showYear) }"
        :disabled="getYearDisable(showYear)"
      )
        option(v-for="year in years" :value="year.value") {{ year.label }}
      span 年

      // month
      select#month.register-form.month(
        name="month"
        v-model="_month"
        v-validate="{ required: required }"
        placeholder="月"
        data-vv-as="月"
        :class="{ noinput: (required == true && !_month), disable: getMonthDisabled(year) }"
        :disabled="getMonthDisabled(year)"
      )
        option(v-for="month in months") {{ month }}
      span 月

      // day
      select#day.register-form.day(
        name="day"
        v-model="_day"
        v-validate="{ required: required }"
        placeholder="日"
        data-vv-as="日"
        :class="{ noinput: (required == true && !_day), disable: getDayDisabled(year, month) }"
        :disabled="getDayDisabled(year, month)"
      )
        option(v-for="day in days") {{ day }}
      span 日

      // hour
      select#hour.register-form.hour(
        name="hour"
        v-model="_hour"
        v-validate="{ required: required }"
        placeholder="時"
        data-vv-as="時"
        :class="{ noinput: (required == true && !_hour), disable: getHourDisabled(year, month, day) }"
        :disabled="getHourDisabled(year, month, day)"
      )
        option(v-for="hour in hours") {{ hour }}
      span 時

      // min
      select#min.register-form.min(
        name="min"
        v-model="_min"
        v-validate="{ required: required }"
        placeholder="分"
        data-vv-as="分"
        :class="{ noinput: (required == true && !_min), disable: getMinDisabled(year, month, day, hour) }"
        :disabled="getMinDisabled(year, month, day, hour)"
      )
        option(v-for="min in mins") {{ min }}
      span 分

    .error-messages
      div(v-if="errors.has('year')")
        label.error-message {{ errors.first('year') }}
      div(v-if="errors.has('month')")
        label.error-message {{ errors.first('month') }}
      div(v-if="errors.has('day')")
        label.error-message {{ errors.first('day') }}
      div(v-if="errors.has('hour')")
        label.error-message {{ errors.first('hour') }}
      div(v-if="errors.has('min')")
        label.error-message {{ errors.first('min') }}
      div(v-if="errors.has('date')")
        label.error-message {{ errors.first('date') }}
      div(v-if="realtimeError != ''")
        label.error-message {{ realtimeError }}
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";

/**
 * 入力要素コンポーネント(年月日時分分割入力)
 *
 * - 年月日時分の分割入力をサポートする
 * - 既存の入力画面のスタイルが適用される
 * - バリデーターは親コンポーネントのものをInjectionして使用する
 *   - そのため、親コンポーネントでveevalidateをnewしておく必要がある
 */
@Component
export default class FormInputSplitDateTime extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 項目名 */
  @Prop({ required: true, type: String })
  keyName!: string;

  /** 必須か */
  @Prop({
    default: () => {
      return false;
    },
    type: Boolean
  })
  required!: boolean;

  /** veevalidateに渡すバリデーション項目名(date用) */
  @Prop({
    default: () => {
      return "年月日";
    },
    type: String
  })
  validationDateKeyName!: string;

  /** 年の入力可否 */
  @Prop({ required: true })
  showYear!: boolean;

  /** 年 */
  @Prop({
    default: ""
  })
  year!: string;

  /** 年の単位 */
  @Prop({ required: true })
  years!: Array<{ label: string; value: number }>;

  /** 月 */
  @Prop({
    default: ""
  })
  month!: string;

  /** 月の単位 */
  @Prop({ required: true })
  months!: Array<string>;

  /** 日 */
  @Prop({
    default: ""
  })
  day!: string;

  /** 日の単位 */
  @Prop({ required: true })
  days!: Array<string>;

  /** 時 */
  @Prop({
    default: ""
  })
  hour!: string;

  /** 時の単位 */
  @Prop({ required: true })
  hours!: Array<string>;

  /** 分 */
  @Prop({
    default: ""
  })
  min!: string;

  /** 分の単位 */
  @Prop({ required: true })
  mins!: Array<string>;

  /** リアルタイムに表示するエラーメッセージ */
  @Prop()
  realtimeError!: string;

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _year(): string {
    if (this.year) {
      return this.year;
    } else {
      return "";
    }
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _year(value: string) {
    this.$emit("update:year", value);
    return;
  }

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _month(): string {
    if (this.month) {
      return this.month;
    } else {
      return "";
    }
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _month(value: string) {
    this.$emit("update:month", value);
    return;
  }

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _day(): string {
    if (this.day) {
      return this.day;
    } else {
      return "";
    }
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _day(value: string) {
    this.$emit("update:day", value);
    return;
  }

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _hour(): string {
    if (this.hour) {
      return this.hour;
    } else {
      return "";
    }
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _hour(value: string) {
    this.$emit("update:hour", value);
    return;
  }

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _min(): string {
    if (this.min) {
      return this.min;
    } else {
      return "";
    }
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _min(value: string) {
    this.$emit("update:min", value);
    return;
  }

  public getYearDisable(showYear: boolean) {
    return !showYear;
  }

  public getMonthDisabled(year: number): boolean {
    if (year) {
      return false;
    } else {
      return true;
    }
  }

  public getDayDisabled(year: number, month: number): boolean {
    if (year && month) {
      return false;
    } else {
      return true;
    }
  }

  public getHourDisabled(year: number, month: number, day: number): boolean {
    if (year && month && day) {
      return false;
    } else {
      return true;
    }
  }

  public getMinDisabled(year: number, month: number, day: number, hour: number): boolean {
    if (year && month && day && hour) {
      return false;
    } else {
      return true;
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.input-split-date-time {
  .register-form {
    &.disable {
      cursor: not-allowed;
      background-color: $btn-disable !important;
      color: $btn-font-disable !important;
      transition: background-color 0.25s ease-out, box-shadow 0.25s ease-out, color 0.25s ease-out;
    }
  }
}
</style>
